import { FormControl, FormHelperText, IconButton, InputAdornment, Stack } from "@mui/material";
import { useTranslations } from "next-intl";
import React from "react";
import { SnackBar } from "@patron/ui/snack-bar";
import { useTheme } from "@patron/utils/theme";
import { CustomTextField } from "@patron/ui/input";
import { VisibilityOff, Visibility } from "@mui/icons-material";
import { validPassword } from "../utils";

interface ResetPasswordFormProps {
  onResetDetails: (val: ResetPasswordFormState) => void;
  data?: any;
}

export interface ResetPasswordFormState {
  confirmPassword: string;
  password: string;
  showPassword: boolean;
  showConfirmPassword: boolean;
}

interface ResetPasswordFormError {
  confirmPassword: string;
  password: string;
}

export const ResetPasswordForm = React.forwardRef((props: ResetPasswordFormProps, ref) => {
  const t = useTranslations("login");
  const { onResetDetails } = props;
  const theme = useTheme();
  const [values, setValues] = React.useState<ResetPasswordFormState>({
    confirmPassword: "",
    password: "",
    showConfirmPassword: false,
    showPassword: false,
  });
  const [formIsSubmitted, setFormIsSubmitted] = React.useState(false);
  const [snackBar, setSnackBar] = React.useState(false);
  const [snackBarMessage, setSnackBarMessage] = React.useState("");
  const [severity, setSeverity] = React.useState("");
  const [formErrors, setFormErrors] = React.useState<ResetPasswordFormError>({
    confirmPassword: t("missingValues", { ns: "feedback" }),
    password: t("missingValues", { ns: "feedback" }),
  });

  const handleClickShowPassword = () => {
    setValues({
      ...values,
      showPassword: !values.showPassword,
    });
  };

  const handleConfirmShowPassword = () => {
    setValues({
      ...values,
      showConfirmPassword: !values.showConfirmPassword,
    });
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleChange = (prop: keyof ResetPasswordFormState) => (event: React.ChangeEvent<HTMLInputElement>) => {
    if (prop === "confirmPassword" || prop === "password") {
      if (prop === "confirmPassword" && event.target.value === "") {
        formErrors.confirmPassword = t("missingValues", { ns: "feedback" });
      } else {
        if (event.target.value === values.password) {
          formErrors.confirmPassword = "";
        } else {
          formErrors.confirmPassword = t("passwordMatch", {
            ns: "feedback",
          });
        }
      }
      if (prop === "password" && event.target.value === "") {
        formErrors.password = t("missingValues", { ns: "feedback" });
      } else {
        const checkPassword = validPassword(values.password);
        if (checkPassword) {
          formErrors.password = "";
        } else {
          formErrors.password = t("passwordMustContain", {
            ns: "feedback",
          });
        }
      }
    }
    setFormErrors({ ...formErrors });
    setValues({ ...values, [prop]: event.target.value });
  };

  React.useImperativeHandle(ref, () => ({
    onSubmit() {
      setFormIsSubmitted(true);
      if (Object.values(formErrors).some((item) => item.length > 0)) {
        setSnackBar(true);
        setSeverity("error");
        setSnackBarMessage(t("missingValues", { ns: "feedback" }));
      } else {
        setFormErrors({ password: "", confirmPassword: "" });
        onResetDetails(values);
      }
    },
  }));

  const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackBar(false);
  };
  return (
    <Stack width="100%" id="LoginForm" spacing={2} padding={1}>
      <Stack width="100%" alignItems="center" spacing={2} padding={1} direction={"column"}>
        <FormControl sx={{ width: "50%" }} variant="outlined">
          <CustomTextField
            id="outlined-password"
            label={t("password")}
            controldisabled={false}
            required
            type={values.showPassword ? "text" : "password"}
            value={values.password}
            error={formIsSubmitted && formErrors.password.length > 0}
            onChange={handleChange("password")}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {values.showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {formIsSubmitted && (
            <FormHelperText
              sx={{
                color: formErrors.password ? theme.appColors.error : "",
              }}
              id="filled-password-helper-text"
            >
              {formErrors.password ? formErrors.password : ""}
            </FormHelperText>
          )}
        </FormControl>
        <FormControl sx={{ width: "50%" }} variant="outlined">
          <CustomTextField
            id="outlined-adornment-confirmPassword"
            label={t("confirmPassword")}
            controldisabled={false}
            required
            type={values.showConfirmPassword ? "text" : "password"}
            error={formIsSubmitted && formErrors.confirmPassword.length > 0}
            value={values.confirmPassword}
            onChange={handleChange("confirmPassword")}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleConfirmShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {values.showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {formIsSubmitted && (
            <FormHelperText
              sx={{
                color: formErrors.password ? theme.appColors.error : "",
              }}
              id="filled-name-helper-text"
            >
              {formErrors.confirmPassword ? formErrors.confirmPassword : ""}
            </FormHelperText>
          )}
        </FormControl>
      </Stack>
      <SnackBar
        openState={snackBar}
        //@ts-ignore
        severity={severity}
        message={snackBarMessage}
        //@ts-ignore
        onClose={handleClose}
      ></SnackBar>
    </Stack>
  );
});

ResetPasswordForm.displayName = "ResetPasswordForm";

export default ResetPasswordForm;
